import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  type Location as RRLocation,
} from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { clearQueryCacheOnUserChange } from "./lib/queryCacheOnLogout";
import { ThemeProvider } from "./components/ThemeProvider";
import ProtectedLayout from "./components/layout/ProtectedLayout";
import StreamDock from "./components/stream/StreamDock";
import { isNotifSection, playChatSound } from "./lib/notificationSound";
import StreamStatusBridge from "./components/stream/StreamStatusBridge";
import KeepaliveHost from "./components/stream/KeepaliveHost";
import UploadQueueToast from "./components/uploads/UploadQueueToast";
import StorageBanner from "./components/consent/StorageBanner";
import "./i18n";
import RouteFallback from "./components/layout/RouteFallback";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ChallengeOtpPage = lazy(() => import("./pages/ChallengeOtpPage"));
const ChallengeSetPasswordPage = lazy(
  () => import("./pages/ChallengeSetPasswordPage"),
);
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ForgotPasswordVerifyPage = lazy(
  () => import("./pages/ForgotPasswordVerifyPage"),
);
const ForgotPasswordResetPage = lazy(
  () => import("./pages/ForgotPasswordResetPage"),
);
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const FirstLoginPage = lazy(() => import("./pages/FirstLoginPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const StreetChatPage = lazy(() => import("./pages/StreetChatPage"));
const PairChatListPage = lazy(() => import("./pages/PairChatListPage"));
const PairChatPage = lazy(() => import("./pages/PairChatPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PostDetailPage = lazy(() => import("./pages/PostDetailPage"));
const SavedLikedPage = lazy(() => import("./pages/SavedLikedPage"));
const GeneralWallPage = lazy(() => import("./pages/GeneralWallPage"));
const FollowingFeedPage = lazy(() => import("./pages/FollowingFeedPage"));
const WorldSearchPage = lazy(() => import("./pages/WorldSearchPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const PrivacySettingsPage = lazy(() => import("./pages/PrivacySettingsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const GoogleCallbackPage = lazy(() => import("./pages/GoogleCallbackPage"));
const MapChatsPage = lazy(() => import("./pages/MapChatsPage"));
const RecentConversationsPage = lazy(
  () => import("./pages/RecentConversationsPage"),
);

const queryClient = new QueryClient();
// Logout (or another account signing in) drops the previous user's cached data.
clearQueryCacheOnUserChange(queryClient);

function NavigationHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === "NAVIGATE") navigate(e.data.url as string);
      // The SW posted a background chat notification for a hidden tab.
      else if (
        e.data?.type === "PLAY_CHAT_SOUND" &&
        isNotifSection(e.data.section)
      )
        playChatSound(e.data.section);
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () =>
      navigator.serviceWorker?.removeEventListener("message", handler);
  }, [navigate]);
  return null;
}

interface NavState {
  backgroundLocation?: RRLocation;
}

function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = (location.state as NavState | null)
    ?.backgroundLocation;

  return (
    <>
      <NavigationHandler />
      <Suspense fallback={<RouteFallback />}>
        <Routes location={backgroundLocation ?? location}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/otp" element={<ChallengeOtpPage />} />
          <Route
            path="/auth/set-password"
            element={<ChallengeSetPasswordPage />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/forgot-password/verify"
            element={<ForgotPasswordVerifyPage />}
          />
          <Route
            path="/forgot-password/reset"
            element={<ForgotPasswordResetPage />}
          />
          <Route path="/first-login" element={<FirstLoginPage />} />
          <Route
            path="/auth/google/callback"
            element={<GoogleCallbackPage />}
          />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Navigate to="/map" replace />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/map-chats" element={<MapChatsPage />} />
            <Route path="/street-chat" element={<StreetChatPage />} />
            <Route path="/pair-chat" element={<PairChatListPage />} />
            <Route path="/pair-chat/:id" element={<PairChatPage />} />
            <Route path="/profile/:userId" element={<ProfilePage />} />
            {/* Owner only: there is no route for someone else's saved posts. */}
            <Route path="/profile/me/saved" element={<SavedLikedPage />} />
            <Route path="/feed" element={<GeneralWallPage />} />
            <Route path="/cooperative" element={<GeneralWallPage />} />
            <Route path="/feed/following" element={<FollowingFeedPage />} />
            <Route path="/feed/search" element={<WorldSearchPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route
              path="/recent-conversations"
              element={<RecentConversationsPage />}
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/privacy" element={<PrivacySettingsPage />} />
            <Route
              path="/settings/change-password"
              element={<ChangePasswordPage />}
            />
            <Route path="/post/:postId" element={<PostDetailPage />} />
            <Route path="/post/:postId/comment/:commentId" element={<PostDetailPage />} />
          </Route>
        </Routes>
      </Suspense>
      {backgroundLocation && (
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<ProtectedLayout />}>
              <Route path="/post/:postId" element={<PostDetailPage />} />
              <Route path="/post/:postId/comment/:commentId" element={<PostDetailPage />} />
            </Route>
          </Routes>
        </Suspense>
      )}
      {/*
        Exactly one of each, inside the router but outside both <Routes>
        trees. ProtectedLayout mounts twice whenever a post modal is open over
        a profile feed: a host per layout would show two cards with duplicated
        audio and tear the session down when the modal closed, and a bridge per
        layout would apply every stream event twice. StreamDock is the one
        floating surface: it hosts the live mini card (PipHost) and the cast cards.
        KeepaliveHost is the keep-alive prompt's one host: a second would
        apply each prompt event twice and show two dialogs.
        UploadQueueToast shows videos still sending, on every screen.
      */}
      <StreamDock />
      <StreamStatusBridge />
      <KeepaliveHost />
      <UploadQueueToast />
      {/* Privacy consent: the device's storage choices, over every route (signed in or not, /login included). */}
      <StorageBanner />
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
